/**
 * Che cosa vede la sentinella, per chi non apre la console.
 *
 * **Il primo passo e' la colonna che conta.** Un elenco di guasti senza «da
 * dove si comincia» diventa rumore nel giro di un mese, e il rumore si spegne:
 * per questo sta in tabella accanto al guasto e non in un dettaglio da aprire.
 *
 * **Si rilegge a mano.** Una pagina che interroga il servizio da sola invecchia
 * male (§ 11), e le condizioni della sentinella non cambiano al secondo: la
 * fase ferma da tre ore lo sara' anche fra un minuto.
 */
import { useCallback, useEffect, useState } from 'react'

import { EmptyState } from '../carta/EmptyState'
import { moment } from '../format'
import { useT } from '../i18n/LocaleProvider'
import { look, type Watch } from '../operations'

export function Operations(): JSX.Element {
  const t = useT()
  const [seen, setSeen] = useState<Watch | null>(null)
  const [wrong, setWrong] = useState<string | null>(null)
  const [reading, setReading] = useState(false)

  /*
    Senza `t` fra le dipendenze, e non per pigrizia: `useT()` restituisce una
    funzione nuova a ogni render, per cui una lettura che dipende da lei si
    riarma da sola all'infinito — e questa pagina interroga il servizio. Il
    guasto senza messaggio si traduce al momento di dipingerlo, dove la lingua
    corrente c'e' comunque.
  */
  const read = useCallback(() => {
    setReading(true)
    void look()
      .then((found) => {
        setSeen(found)
        setWrong(null)
      })
      .catch((error: unknown) => {
        setWrong(error instanceof Error ? error.message : '')
      })
      .finally(() => setReading(false))
  }, [])

  useEffect(() => {
    read()
  }, [read])

  return (
    <main className="esercizio">
      <p className="tenue" role="status">
        {seen === null
          ? t('operations.loading')
          : t('operations.checkedAt', {
              when: moment(seen.checked_at),
              n: seen.jobs_in_flight,
            })}
      </p>
      <p>
        <button type="button" onClick={read} disabled={reading}>
          {t('operations.recheck')}
        </button>
      </p>

      {wrong !== null && <p role="alert">{wrong === '' ? t('operations.readFailed') : wrong}</p>}

      {seen !== null && seen.concerns.length === 0 && (
        // La quiete si dichiara con la sua data: senza, non si distingue da una
        // sentinella spenta.
        // La data non si ripete qui: sta nella riga di stato, che c'e'
        // sempre. Scritta due volte, una delle due prima o poi mentira'.
        <EmptyState title={t('operations.quietTitle')} action={t('operations.quietAction')} />
      )}

      {seen !== null && seen.concerns.length > 0 && (
        <div className="tabella-larga">
          <table>
            <thead>
              <tr>
                <th>{t('operations.colWhat')}</th>
                <th>{t('operations.colDetail')}</th>
                <th>{t('operations.colFirstStep')}</th>
              </tr>
            </thead>
            <tbody>
              {seen.concerns.map((one, index) => (
                <tr key={`${one.what}-${index}`}>
                  <td>{one.what}</td>
                  <td>{one.detail}</td>
                  <td>{one.first_step}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  )
}
